import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { useLayerProperitesContext } from '../context/LayerPropertiesContext';
import { useLegendUI } from '../context/MapLegendContext';
import { buildSingleConfig } from '../views/MapPortal/LayerProperties/Symbology/symbologyConfig.utils';

// Long enough to skip a rebuild per slider tick, short enough to feel live.
const PREVIEW_DEBOUNCE_MS = 200;

interface LayerRow {
  id?: unknown;
}
type LayerPropertiesState = ReturnType<typeof useLayerProperitesContext>;

export interface SymbologyPreview {
  fields: ReturnType<typeof previewFieldsOf>;
  signature: string;
  // Set once saved: the row it was saved over, so the preview ends when the refetched row replaces it.
  heldRow?: LayerRow;
  isHeld: boolean;
}

const previewFieldsOf = (ctx: LayerPropertiesState) => ({
  id: ctx.id,
  fill: ctx.fill,
  fillType: ctx.fillType,
  fillOpacity: ctx.fillOpacity,
  outline: ctx.outline,
  outlineType: ctx.outlineType,
  outlineOpacity: ctx.outlineOpacity,
  outlineWeight: ctx.outlineWeight,
  symbology: ctx.symbology,
  // Single edits only touch the flat fields; the renderer reads the config first, so build it as Save does.
  symbologyConfig: ctx.symbology === 'single' ? buildSingleConfig(ctx) : ctx.symbologyConfig,
  opacity: ctx.opacity,
  selectedIcon: ctx.selectedIcon,
  iconType: ctx.iconType,
  iconSize: ctx.iconSize,
  iconRotation: ctx.iconRotation,
  iconUploadKind: ctx.iconUploadKind,
});

export const applySymbologyPreview = <T extends LayerRow>(layers: T[], preview: SymbologyPreview | null): T[] => {
  if (!preview || preview.fields.id == null) return layers;
  const { fields, heldRow, isHeld } = preview;
  if (isHeld && (!heldRow || !layers.includes(heldRow as T))) return layers;
  return layers.map((layer) => (layer.id === fields.id ? { ...layer, ...fields } : layer));
};

// Live-previews unsaved Layer Properties symbology. The open-edge snapshot tells a cancel or discard (back to it)
// from a save (still differs), so a save keeps its preview until the refetch lands instead of flashing the old style.
export const useSymbologyPreview = <T extends LayerRow>({
  layers,
  setPreview,
}: {
  layers: T[];
  setPreview: Dispatch<SetStateAction<SymbologyPreview | null>>;
}) => {
  const ctx = useLayerProperitesContext();
  const { modalVisible } = useLegendUI();
  const fields = previewFieldsOf(ctx);
  const signature = JSON.stringify(fields);
  const openSignatureRef = useRef<string | null>(null);
  const latestRef = useRef({ fields, signature, layers });
  latestRef.current = { fields, signature, layers };

  // Callers seed the context before opening the modal, as LayerPropertiesModal's own snapshot relies on.
  useEffect(() => {
    if (modalVisible) {
      if (openSignatureRef.current === null) openSignatureRef.current = latestRef.current.signature;
      return;
    }
    const openSignature = openSignatureRef.current;
    if (openSignature === null) return;
    openSignatureRef.current = null;
    const latest = latestRef.current;
    if (latest.signature === openSignature) {
      setPreview(null);
      return;
    }
    const heldRow = latest.layers.find((layer) => layer.id === latest.fields.id);
    setPreview({ fields: latest.fields, signature: latest.signature, heldRow, isHeld: true });
  }, [modalVisible, setPreview]);

  useEffect(() => {
    if (!modalVisible) return;
    const timer = window.setTimeout(() => {
      // Unedited (or edited back) layers render their saved row, so opening the modal rebuilds nothing.
      if (signature === openSignatureRef.current) {
        setPreview(null);
        return;
      }
      setPreview((previous) =>
        previous?.signature === signature ? previous : { fields: latestRef.current.fields, signature, isHeld: false },
      );
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [modalVisible, signature, setPreview]);

  // An unsaved preview leaves with its host; a saved one still ends when its refetch lands.
  useEffect(() => () => setPreview((previous) => (previous?.isHeld ? previous : null)), [setPreview]);
};
